import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft, 
  Star, 
  Clock, 
  MapPin, 
  Heart,
  Plus,
  Minus,
  ShoppingCart,
  Leaf
} from "lucide-react";
import axios from "axios";
import { getBackendUrl } from "../utils/api";

const API = `${getBackendUrl()}/api`;

const RestaurantPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({ items: [] });
  const [favorites, setFavorites] = useState({ dishes: [], restaurant: false });
  const [user, setUser] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("fleety_user");
    if (!storedUser) {
      navigate("/login");
      return;
    }
    setUser(JSON.parse(storedUser));
  }, [navigate]);

  useEffect(() => {
    if (user) {
      fetchRestaurant();
      fetchCart();
      fetchFavorites();
    }
  }, [id, user]);

  const fetchRestaurant = async () => {
    try {
      // Get delivery location for distance calculation
      let params = {};
      const savedAddress = localStorage.getItem("fleety_delivery_address");
      if (savedAddress) {
        const address = JSON.parse(savedAddress);
        if (address.lat && address.lng) {
          params = { lat: address.lat, lng: address.lng };
        } else if (address.pincode) {
          params = { pincode: address.pincode };
        }
      } else if (user?.pincode) {
        params = { pincode: user.pincode };
      }
      
      const response = await axios.get(`${API}/restaurants/${id}`, { params });
      setRestaurant(response.data);
    } catch (error) {
      console.error("Error fetching restaurant:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart/${user.id}`);
      setCart(response.data);
    } catch (error) {
      console.error("Error fetching cart:", error);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites/${user.id}`);
      const favData = response.data;
      // Backend returns {restaurants: [...], dishes: [...]}
      const dishFavs = favData.dishes?.map(d => d.id) || [];
      const restFav = favData.restaurants?.some(r => r.id === id) || false;
      setFavorites({ dishes: dishFavs, restaurant: restFav });
    } catch (error) {
      console.error("Error fetching favorites:", error);
    }
  };

  const getItemQuantity = (itemId) => {
    const cartItem = cart.items?.find(item => item.item_id === itemId);
    return cartItem ? cartItem.quantity : 0;
  };

  const updateCart = async (item, quantity) => {
    console.log("updateCart called:", { item, quantity, user_id: user?.id });
    try {
      const payload = {
        user_id: user.id,
        item_id: item.id,
        restaurant_id: id,
        name: item.name,
        price: item.price,
        quantity: quantity,
        image: item.image_url || item.image || ""
      };
      console.log("Cart update payload:", payload);
      
      const response = await axios.post(`${API}/cart/update`, payload);
      console.log("Cart update response:", response.data);
      
      if (response.data.success === false) {
        if (window.confirm(response.data.message + " Do you want to clear it?")) {
          await axios.delete(`${API}/cart/${user.id}`);
          await updateCart(item, quantity);
        }
        return;
      }
      
      fetchCart();
    } catch (error) {
      console.error("Error updating cart:", error);
      alert("Failed to add item to cart. Please try again.");
    }
  };

  const toggleRestaurantFavorite = async () => {
    try {
      if (favorites.restaurant) {
        // Use DELETE endpoint
        await axios.delete(`${API}/favorites/${user.id}/restaurant/${id}`);
      } else {
        // Use POST endpoint
        await axios.post(`${API}/favorites`, {
          user_id: user.id,
          item_type: "restaurant",
          item_id: id
        });
      }
      setFavorites(prev => ({ ...prev, restaurant: !prev.restaurant }));
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  const toggleDishFavorite = async (itemId) => {
    try {
      const isFav = favorites.dishes.includes(itemId);
      if (isFav) {
        // Use DELETE endpoint
        await axios.delete(`${API}/favorites/${user.id}/dish/${itemId}`);
        setFavorites(prev => ({ ...prev, dishes: prev.dishes.filter(d => d !== itemId) }));
      } else {
        // Use POST endpoint
        await axios.post(`${API}/favorites`, {
          user_id: user.id,
          item_type: "dish",
          item_id: itemId,
          restaurant_id: id
        });
        setFavorites(prev => ({ ...prev, dishes: [...prev.dishes, itemId] }));
      }
    } catch (error) {
      console.error("Error toggling dish favorite:", error);
    }
  };

  const cartTotal = cart.items?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0;
  const cartCount = cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  // Group menu by category
  const menuByCategory = restaurant?.menu?.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {}) || {};

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#3FA9F5] border-t-transparent"></div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p>Restaurant not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="restaurant-page">
      {/* Top Spacer for mobile */}
      <div className="h-6 bg-gray-50"></div>
      
      {/* Header Image */}
      <div className="relative h-48 md:h-64">
        <img 
          src={restaurant.image_url || restaurant.image} 
          alt={restaurant.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        
        {/* Back Button */}
        <button 
          onClick={() => navigate("/home")}
          className="absolute top-4 left-4 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg"
          data-testid="back-btn"
        >
          <ArrowLeft size={20} className="text-gray-700" />
        </button>
        
        {/* Favorite Button */}
        <button 
          onClick={toggleRestaurantFavorite}
          className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg"
          data-testid="favorite-restaurant-btn"
        >
          <Heart 
            size={20} 
            className={favorites.restaurant ? "text-red-500 fill-red-500" : "text-gray-400"} 
          />
        </button>
      </div>

      {/* Restaurant Info */}
      <div className="bg-white px-4 py-4 shadow-sm">
        <h1 className="text-2xl font-bold text-gray-900" data-testid="restaurant-name">
          {restaurant.name}
        </h1>
        <p className="text-gray-500">{restaurant.cuisine}</p>
        
        <div className="flex items-center gap-4 mt-3 text-sm">
          <div className="flex items-center gap-1">
            <Star size={16} className="text-yellow-500 fill-yellow-500" />
            <span className="font-medium">{restaurant.rating}</span>
          </div>
          <div className="flex items-center gap-1 text-gray-500">
            <Clock size={16} />
            <span>{restaurant.delivery_time}</span>
          </div>
          <div className="flex items-center gap-1 text-gray-500">
            <MapPin size={16} />
            <span>{restaurant.distance} km</span>
          </div>
        </div>
      </div>

      {/* Menu */}
      <div className="px-4 py-6">
        {Object.entries(menuByCategory).map(([category, items]) => (
          <div key={category} className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3" data-testid={`category-${category}`}>
              {category}
            </h2>
            <div className="space-y-3">
              {items.map((item) => {
                const quantity = getItemQuantity(item.id);
                const isDishFavorite = favorites.dishes.includes(item.id);
                
                return (
                  <div 
                    key={item.id} 
                    className="bg-white rounded-xl p-4 shadow-sm flex gap-4"
                    data-testid={`menu-item-${item.id}`}
                  >
                    {/* Item Info */}
                    <div className="flex-1">
                      <div className="flex items-start gap-2">
                        <div className={`w-4 h-4 border-2 ${item.is_veg ? 'border-green-600' : 'border-red-600'} rounded-sm flex items-center justify-center mt-1`}>
                          <div className={`w-2 h-2 rounded-full ${item.is_veg ? 'bg-green-600' : 'bg-red-600'}`}></div>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-gray-900">{item.name}</h3>
                            {item.is_bestseller && (
                              <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                                Bestseller
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                          <p className="text-base font-semibold text-gray-900 mt-2">₹{item.price}</p>
                        </div>
                        <button 
                          onClick={() => toggleDishFavorite(item.id)}
                          className="p-1"
                          data-testid={`favorite-dish-${item.id}`}
                        >
                          <Heart 
                            size={18} 
                            className={isDishFavorite ? "text-red-500 fill-red-500" : "text-gray-300"} 
                          />
                        </button>
                      </div>
                    </div>
                    
                    {/* Item Image & Add Button */}
                    <div className="relative">
                      {(item.image_url || item.image) ? (
                        <img 
                          src={item.image_url || item.image} 
                          alt={item.name}
                          className="w-24 h-24 rounded-lg object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className={`w-24 h-24 rounded-lg bg-gradient-to-br from-orange-100 to-orange-50 flex items-center justify-center ${(item.image_url || item.image) ? 'hidden' : 'flex'}`}
                      >
                        <span className="text-3xl">🍽️</span>
                      </div>
                      
                      {/* Add/Quantity Controls */}
                      <div className="absolute -bottom-3 left-1/2 -translate-x-1/2">
                        {quantity === 0 ? (
                          <Button
                            onClick={() => updateCart(item, 1)}
                            className="bg-white text-[#3FA9F5] border-2 border-[#3FA9F5] hover:bg-[#3FA9F5] hover:text-white px-6 py-1 h-8 rounded-lg font-semibold text-sm shadow-md"
                            data-testid={`add-btn-${item.id}`}
                          >
                            ADD
                          </Button>
                        ) : (
                          <div className="flex items-center gap-2 bg-[#3FA9F5] rounded-lg px-2 py-1 shadow-md">
                            <button 
                              onClick={() => updateCart(item, quantity - 1)}
                              className="text-white"
                              data-testid={`minus-btn-${item.id}`}
                            >
                              <Minus size={16} />
                            </button>
                            <span className="text-white font-semibold min-w-[20px] text-center" data-testid={`quantity-${item.id}`}>
                              {quantity}
                            </span>
                            <button 
                              onClick={() => updateCart(item, quantity + 1)}
                              className="text-white"
                              data-testid={`plus-btn-${item.id}`}
                            >
                              <Plus size={16} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Cart Footer */}
      {cartCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-[#3FA9F5] px-4 py-4 shadow-lg z-50" data-testid="cart-footer">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <div className="text-white">
              <span className="font-semibold">{cartCount} {cartCount === 1 ? 'item' : 'items'}</span>
              <span className="mx-2">|</span>
              <span className="font-bold">₹{cartTotal}</span>
            </div>
            <Button
              onClick={() => navigate("/cart")}
              className="bg-white text-[#3FA9F5] hover:bg-gray-100 font-semibold px-6"
              data-testid="view-cart-btn"
            >
              <ShoppingCart size={18} className="mr-2" />
              View Cart
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RestaurantPage;
